import React, { useState } from 'react';
import { Table, Button } from 'react-bootstrap';
import IntegerFmt from '../../components/common/typeFormatting/IntegerFmt';
import Dollars from '../../components/common/typeFormatting/Dollars';
import DateFmt from '../../components/common/typeFormatting/Date';
import PercentFmt from '../../components/common/typeFormatting/PercentFmt';
import { EmployeeOptionsStatement } from '../types/employee-statement';
import '../styles/options-tables.css';

interface SummaryTableProps {
  data: EmployeeOptionsStatement;
}

export const SummaryTable: React.FC<SummaryTableProps> = ({ data }) => {
  const [showFullyExercised, setShowFullyExercised] = useState(false);

  // Separate options into active and fully exercised
  const allOptions = data.activeOptions.options;
  const activeOptions = allOptions.filter(opt => opt.exercisedShares < opt.totalShares);
  const fullyExercisedOptions = allOptions.filter(opt => opt.exercisedShares === opt.totalShares);

  const renderOptionsTable = (options: typeof allOptions, title?: string) => {
    if (options.length === 0) return null;

    // Calculate totals
    const totals = {
      totalShares: options.reduce((sum, opt) => sum + opt.totalShares, 0),
      exercisedShares: options.reduce((sum, opt) => sum + opt.exercisedShares, 0),
      vestedShares: options.reduce((sum, opt) => sum + opt.vestedShares, 0),
      vestedRemaining: options.reduce((sum, opt) => sum + (opt.vestedShares - opt.exercisedShares), 0),
      vestedValue: options.reduce((sum, opt) => sum + opt.vestedValue, 0)
    };

    return (
      <>
        {title && <h5 className="mt-4 mb-3">{title}</h5>}
        <Table hover responsive className="options-table">
          <thead>
            <tr>
              <th style={{ width: '110px' }}>Issue Date</th>
              <th>Description</th>
              <th className="header-numeric-col" style={{ width: '90px' }}>Options Issued</th>
              <th className="header-numeric-col" style={{ width: '80px' }}>Vested %</th>
              <th className="header-numeric-col" style={{ width: '90px' }}>Exercised Options</th>
              <th className="header-numeric-col" style={{ width: '100px', backgroundColor: 'rgb(243, 243, 243)' }}>Vested Options Remaining</th>
              <th className="header-numeric-col" style={{ width: '90px' }}>Strike Price</th>
              <th className="header-numeric-col" style={{ width: '90px', backgroundColor: 'rgb(243, 243, 243)' }}>Price Difference</th>
              <th className="header-numeric-col" style={{ width: '100px', backgroundColor: 'rgb(235, 235, 235)' }}>Current Vested Value</th>
              <th style={{ width: '110px' }}>Expiration Date</th>
            </tr>
          </thead>
          <tbody>
            {options.map((option) => (
              <tr key={option.optionAgreementId}>
                <td>
                  <DateFmt>{option.issueDate}</DateFmt>
                </td>
                <td>{option.description}</td>
                <td>
                  <IntegerFmt>{option.totalShares}</IntegerFmt>
                </td>
                <td>
                  <PercentFmt>{option.vestedPercentage}</PercentFmt>
                </td>
                <td>
                  <IntegerFmt>{option.exercisedShares}</IntegerFmt>
                </td>
                <td style={{ backgroundColor: 'rgb(243, 243, 243)' }}>
                  <IntegerFmt>{option.vestedShares - option.exercisedShares}</IntegerFmt>
                </td>
                <td>
                  <Dollars>{option.strikePrice}</Dollars>
                </td>
                <td style={{ backgroundColor: 'rgb(243, 243, 243)' }}>
                  <Dollars>{option.priceDifference}</Dollars>
                </td>
                <td style={{ backgroundColor: 'rgb(235, 235, 235)' }}>
                  <Dollars>{option.vestedValue}</Dollars>
                </td>
                <td>
                  <DateFmt>{option.expirationDate}</DateFmt>
                </td>
              </tr>
            ))}
            {/* Totals Row */}
            <tr style={{ fontWeight: 600, borderTop: '2px solid #dee2e6' }}>
              <td></td>
              <td>Total</td>
              <td>
                <IntegerFmt>{totals.totalShares}</IntegerFmt>
              </td>
              <td></td>
              <td>
                <IntegerFmt>{totals.exercisedShares}</IntegerFmt>
              </td>
              <td style={{ backgroundColor: 'rgb(243, 243, 243)' }}>
                <IntegerFmt>{totals.vestedRemaining}</IntegerFmt>
              </td>
              <td></td>
              <td style={{ backgroundColor: 'rgb(243, 243, 243)' }}></td>
              <td style={{ backgroundColor: 'rgb(235, 235, 235)' }}>
                <Dollars>{totals.vestedValue}</Dollars>
              </td>
              <td></td>
            </tr>
          </tbody>
        </Table>
      </>
    );
  };

  return (
    <div>
      {renderOptionsTable(activeOptions)}

      {fullyExercisedOptions.length > 0 && (
        <div className="mt-4">
          <Button
            variant="link"
            onClick={() => setShowFullyExercised(!showFullyExercised)}
            style={{ padding: 0, textDecoration: 'none', color: '#1976d2' }}
          >
            {showFullyExercised ? '▼' : '▶'} Fully Exercised Options ({fullyExercisedOptions.length})
          </Button>
          {showFullyExercised && renderOptionsTable(fullyExercisedOptions)}
        </div>
      )}
    </div>
  );
};
